import React, { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

interface User {
  id: number;
  username: string;
  email: string;
  role: string;
  companyLogo?: string | null;
  profilePhoto?: string;
  photo?: string;
  profile_photo?: string;
  photo_url?: string;
  branch_id?: number | null;
  branch?: {
    id?: number;
    name?: string;
    logo_url?: string | null;
  } | null;
  staffProfile?: {
    id?: number;
    can_access_daily_ops?: boolean | null;
    tab_permissions?: Record<string, boolean> | null;
    photo_path?: string | null;
    photo_url?: string | null;
  } | null;
  can_access_daily_ops?: boolean | null;
  tab_permissions?: Record<string, boolean> | null;
  first_name: string;
  last_name: string;
  phone?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (username: string, password: string) => Promise<boolean | { requiresBiometric: true; tempToken: string; username: string }>;
  loginWithToken: (accessToken: string, userPayload?: any) => Promise<boolean>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  token: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const normalizeApiBase = (rawBase: string): string => {
  const trimmed = (rawBase || '').trim();
  if (!trimmed) return '';

  try {
    const parsed = new URL(trimmed);
    if (parsed.hostname === 'localhost') {
      parsed.hostname = '127.0.0.1';
    }
    return parsed.toString().replace(/\/$/, '');
  } catch {
    return trimmed.replace('://localhost', '://127.0.0.1').replace(/\/$/, '');
  }
};

const configuredApiBase = normalizeApiBase((process as any).env.REACT_APP_API_BASE_URL || '');
export const API_BASE = configuredApiBase ||
  ((window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'http://127.0.0.1:8000'
    : window.location.origin);

const normalizeMediaUrl = (value?: string | null): string | undefined => {
  const raw = (value || '').trim();
  if (!raw) return undefined;

  if (raw.startsWith('/api/files/')) {
    return `${API_BASE}${raw}`;
  }
  if (raw.startsWith('api/files/')) {
    return `${API_BASE}/${raw}`;
  }
  if (/^https?:\/\//i.test(raw)) {
    try {
      const parsed = new URL(raw);
      const isLocal = ['localhost', '127.0.0.1', '0.0.0.0'].includes(parsed.hostname);
      if (isLocal && parsed.pathname.startsWith('/api/files/')) {
        return `${API_BASE}${parsed.pathname}${parsed.search}`;
      }
      return raw;
    } catch {
      return raw;
    }
  }
  return raw;
};

const normalizeUserMedia = (input: any): any => {
  if (!input || typeof input !== 'object') return input;
  return {
    ...input,
    companyLogo: normalizeMediaUrl(input.companyLogo),
    profilePhoto: normalizeMediaUrl(input.profilePhoto),
    photo: normalizeMediaUrl(input.photo),
    profile_photo: normalizeMediaUrl(input.profile_photo),
    photo_url: normalizeMediaUrl(input.photo_url),
    branch: input.branch
      ? {
          ...input.branch,
          logo_url: normalizeMediaUrl(input.branch.logo_url),
        }
      : input.branch,
    staffProfile: input.staffProfile
      ? {
          ...input.staffProfile,
          photo_path: normalizeMediaUrl(input.staffProfile.photo_path) || input.staffProfile.photo_path,
          photo_url: normalizeMediaUrl(input.staffProfile.photo_url),
        }
      : input.staffProfile,
  };
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const lastNetworkToastAtRef = useRef(0);

  const refreshUser = useCallback(async () => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) return;

    axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
    const meResponse = await axios.get(`${API_BASE}/api/auth/me`);
    const verifiedUser = normalizeUserMedia(meResponse.data);
    if (verifiedUser?.role) {
      setUser(verifiedUser);
      localStorage.setItem('user', JSON.stringify(verifiedUser));
    }
  }, []);

  // Configure axios defaults and restore auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      let parsedStoredUser: User | null = null;

      if (storedUser) {
        try {
          parsedStoredUser = normalizeUserMedia(JSON.parse(storedUser));
          setUser(parsedStoredUser);
        } catch {
          localStorage.removeItem('user');
        }
      }

      if (storedToken) {
        setToken(storedToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;

        if (!storedUser) {
          // Avoid replaying stale token-only sessions into a logout loop.
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          setToken(null);
        } else {
          const needsHydration = !parsedStoredUser?.branch?.logo_url;
          if (needsHydration) {
            try {
              const meResponse = await axios.get(`${API_BASE}/api/auth/me`);
              const verifiedUser = normalizeUserMedia(meResponse.data);
              if (verifiedUser?.role) {
                setUser(verifiedUser);
                localStorage.setItem('user', JSON.stringify(verifiedUser));
              }
            } catch (error) {
              console.error('[AUTH] Profile hydration failed', {
                status: (error as any)?.response?.status,
              });
            }
          }
        }

        if (storedUser) {
          try {
            await refreshUser();
          } catch (error) {
            console.error('[AUTH] Profile refresh failed', {
              status: (error as any)?.response?.status,
            });
          }
        }
      }

      setLoading(false);
    };

    initializeAuth();
  }, [refreshUser]);

  useEffect(() => {
    if (!user) return;

    const syncAuthUser = () => {
      void refreshUser().catch((error) => {
        console.error('[AUTH] User sync failed', {
          status: (error as any)?.response?.status,
        });
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncAuthUser();
      }
    };

    const handlePermissionsUpdated = () => {
      syncAuthUser();
    };

    const handleStoragePermissionsUpdated = (event: StorageEvent) => {
      if (event.key === 'staff-permissions-updated') {
        syncAuthUser();
      }
    };

    window.addEventListener('focus', syncAuthUser);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('staff-permissions-updated', handlePermissionsUpdated);
    window.addEventListener('storage', handleStoragePermissionsUpdated);

    return () => {
      window.removeEventListener('focus', syncAuthUser);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('staff-permissions-updated', handlePermissionsUpdated);
      window.removeEventListener('storage', handleStoragePermissionsUpdated);
    };
  }, [refreshUser, user]);

  // Request/response interceptors for auth + network diagnostics
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const fullUrl = `${config.baseURL || ''}${config.url || ''}`;
        console.info('[API REQUEST]', {
          method: config.method,
          baseURL: config.baseURL || API_BASE,
          url: config.url,
          fullUrl,
        });
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        lastNetworkToastAtRef.current = 0;
        return response;
      },
      (error) => {
        const config = error?.config || {};

        if (!error.response) {
          const now = Date.now();
          const COOLDOWN_MS = 5000;
          const isConnectionRefused =
            String(error?.message || '').toLowerCase().includes('network error') &&
            String(error?.stack || '').toLowerCase().includes('err_connection_refused');
          if (now - lastNetworkToastAtRef.current > COOLDOWN_MS) {
            lastNetworkToastAtRef.current = now;
            toast.error(
              isConnectionRefused
                ? 'Backend is not running on 127.0.0.1:8000. Start it using ./start_backend.sh'
                : 'Network Error: Unable to reach backend API', {
              id: 'backend-network-error',
            });
          }
          return Promise.reject(error);
        }

        // FIX: Don't auto-logout on 401 if it's the login endpoint itself
        if (error.response?.status === 401) {
          const isLoginEndpoint = config.url?.includes('/api/auth/login');
          if (!isLoginEndpoint) {
            logout();
            toast.error('Session expired. Please login again.');
          }
        }

        console.error('[API RESPONSE ERROR]', { status: error.response?.status });

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  const login = async (username: string, password: string): Promise<boolean | { requiresBiometric: true; tempToken: string; username: string }> => {
    try {
      const normalizedUsername = (username || '').trim();

      // FIX: Clear any stale/expired tokens BEFORE login attempt
      delete axios.defaults.headers.common['Authorization'];
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setToken(null);
      setUser(null);

      const formData = new FormData();
      formData.append('username', normalizedUsername);
      formData.append('password', password);

      const loginUrl = `${API_BASE}/api/auth/login`;
      const response = await axios.post(loginUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        // Explicitly prevent Authorization header on login request
        transformRequest: [(data, headers) => {
          delete headers['Authorization'];
          return data;
        }],
      });

      // --- Strict 2-step MFA gate ------------------------------------
      // Password is valid but this Super Admin has biometric login
      // enabled: the backend deliberately withholds the real access
      // token and instead returns a short-lived temp_token. Do NOT treat
      // this as a completed login — the caller (Login.tsx) must trigger
      // the WebAuthn ceremony and complete it via /biometric/login/complete
      // before any token is persisted.
      if (response.data?.status === 'requires_biometric' && response.data?.temp_token) {
        return {
          requiresBiometric: true,
          tempToken: response.data.temp_token,
          username: response.data.username || normalizedUsername,
        };
      }

      const { access_token } = response.data;

      if (!access_token) {
        throw new Error('Missing access token in login response');
      }

      // Persist token first for subsequent protected calls
      setToken(access_token);
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      // Mandatory verification call to prevent half-logged-in state
      try {
        const meUrl = `${API_BASE}/api/auth/me`;
        const loginUser = normalizeUserMedia(response.data?.user);

        const meResponse = await axios.get(meUrl);
        const verifiedUser = normalizeUserMedia(meResponse.data);
        if (!verifiedUser?.role) {
          throw new Error('User role missing in profile response');
        }
        const mergedUser = normalizeUserMedia({ ...(loginUser || {}), ...verifiedUser });
        setUser(mergedUser);
        localStorage.setItem('user', JSON.stringify(mergedUser));
      } catch (meError: any) {
        setUser(null);
        setToken(null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        delete axios.defaults.headers.common['Authorization'];

        const meErrorMessage =
          meError?.response?.data?.detail ||
          meError?.message ||
          'Unable to verify user session after login';
        throw new Error(meErrorMessage);
      }

      toast.success('Login successful!');
      return true;
    } catch (error: any) {
      const errorMessage = error?.message || error.response?.data?.detail || 'Login failed';
      toast.error(errorMessage);
      return false;
    }
  };

  // Persists a JWT obtained via an alternate ceremony (WebAuthn biometric
  // login or backup-recovery-code login) without re-running the
  // password flow. Mirrors the token-persist + /api/auth/me verification
  // steps at the end of login().
  const loginWithToken = async (accessToken: string, userPayload?: any): Promise<boolean> => {
    try {
      if (!accessToken) {
        throw new Error('Missing access token');
      }
      setToken(accessToken);
      localStorage.setItem('token', accessToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

      const meResponse = await axios.get(`${API_BASE}/api/auth/me`);
      const verifiedUser = normalizeUserMedia(meResponse.data);
      if (!verifiedUser?.role) {
        throw new Error('User role missing in profile response');
      }
      const mergedUser = normalizeUserMedia({ ...(userPayload || {}), ...verifiedUser });
      setUser(mergedUser);
      localStorage.setItem('user', JSON.stringify(mergedUser));
      toast.success('Login successful!');
      return true;
    } catch (error: any) {
      setUser(null);
      setToken(null);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      delete axios.defaults.headers.common['Authorization'];
      const errorMessage = error?.message || error?.response?.data?.detail || 'Login failed';
      toast.error(errorMessage);
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    toast.success('Logged out successfully');
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    loading,
    login,
    loginWithToken,
    logout,
    refreshUser,
    token,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
